import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WidgetConfig } from './DraggableWidget';

interface WidgetConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  widget: WidgetConfig | null;
  onSave: (widget: WidgetConfig) => void;
}

export const WidgetConfigModal: React.FC<WidgetConfigModalProps> = ({
  isOpen,
  onClose,
  widget,
  onSave
}) => {
  const [title, setTitle] = useState('');
  const [size, setSize] = useState<'small' | 'medium' | 'large' | 'full'>('medium');
  const [chartType, setChartType] = useState('');
  const [color, setColor] = useState('hsl(var(--primary))');

  useEffect(() => {
    if (widget) {
      setTitle(widget.title);
      setSize(widget.size);
      setChartType(widget.config?.chartType || '');
      setColor(widget.config?.color || 'hsl(var(--primary))');
    }
  }, [widget]);

  const handleSave = () => {
    if (!widget) return;

    const updatedWidget: WidgetConfig = {
      ...widget,
      title,
      size,
      config: {
        ...widget.config,
        chartType: widget.type === 'chart' ? chartType : undefined,
        color,
      }
    };

    onSave(updatedWidget);
    onClose();
  };

  const chartOptions = [
    { value: 'sales', label: 'Graphique des ventes détaillé' },
    { value: 'inventory', label: 'État de l\'inventaire' },
    { value: 'payment-methods', label: 'Top produits vendus' },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Configurer le widget</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Titre</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre du widget"
            />
          </div>

          <div>
            <Label htmlFor="size">Taille</Label>
            <Select value={size} onValueChange={(value: any) => setSize(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Petit (1 colonne)</SelectItem>
                <SelectItem value="medium">Moyen (2 colonnes)</SelectItem>
                <SelectItem value="large">Grand (3 colonnes)</SelectItem>
                <SelectItem value="full">Pleine largeur</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="color">Couleur du thème</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hsl(var(--primary))">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-primary"></div>
                    Primaire
                  </div>
                </SelectItem>
                <SelectItem value="hsl(142 76% 36%)">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-green-600"></div>
                    Vert
                  </div>
                </SelectItem>
                <SelectItem value="hsl(221 83% 53%)">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-blue-500"></div>
                    Bleu
                  </div>
                </SelectItem>
                <SelectItem value="hsl(32 95% 60%)">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-orange-400"></div>
                    Orange
                  </div>
                </SelectItem>
                <SelectItem value="hsl(0 84% 60%)">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-red-500"></div>
                    Rouge
                  </div>
                </SelectItem>
                <SelectItem value="hsl(271 91% 65%)">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded bg-purple-500"></div>
                    Violet
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {widget?.type === 'chart' && (
            <div>
              <Label htmlFor="chart-type">Type de graphique</Label>
              <Select value={chartType} onValueChange={setChartType}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un graphique" />
                </SelectTrigger>
                <SelectContent>
                  {chartOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSave}>
            Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};